import { useState } from 'react';

// Web Admin → Images: pictures for the pages (backend site_images.py). Each
// has a permanent address, /images/<slug>.jpg. Uploads are made web-sized
// and lose their camera data (including GPS location) on the way in. Here an
// editor inserts an image into a page's text, adds, replaces, re-describes or
// deletes one (only when no page uses it). PhotoPicker chooses one for a
// home page photo. The list comes from useSiteImages.

const MAX_BYTES = 20 * 1024 * 1024;

// As site_documents.slugify: "Sidecar at Wiscombe" → "sidecar-at-wiscombe".
function slugify(title) {
  return title.toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/^-+|-+$/g, '').slice(0, 60).replace(/-+$/, '');
}

function size(bytes) {
  return bytes >= 1024 * 1024 ? `${(bytes / 1024 / 1024).toFixed(1)} MB` : `${Math.max(1, Math.round(bytes / 1024))} KB`;
}

// The address plus its version, so a replaced picture shows at once here.
const thumb = img => `${img.url}?v=${img.version}`;

async function send(url, session, onExpired, options) {
  const res = await fetch(url, { ...options, headers: { Authorization: `Bearer ${session.session_token}`, ...options.headers } })
    .catch(() => null);
  if (res && res.status === 401) { onExpired(); return null; }
  const data = res ? await res.json().catch(() => ({})) : {};
  return { ok: !!(res && res.ok), data };
}

function AddForm({ session, onExpired, onDone, onCancel }) {
  const [title, setTitle] = useState('');
  const [slug, setSlug] = useState('');
  const [slugEdited, setSlugEdited] = useState(false);
  const [file, setFile] = useState(null);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  const pick = e => {
    const f = e.target.files[0] || null;
    setError(f && f.size > MAX_BYTES ? 'That file is over 20 MB.' : '');
    setFile(f);
  };

  const submit = async e => {
    e.preventDefault();
    setSaving(true);
    setError('');
    const form = new FormData();
    form.append('image', file);
    form.append('title', title);
    form.append('slug', slug);
    const r = await send('/api/site-admin/images', session, onExpired, { method: 'POST', body: form });
    setSaving(false);
    if (!r) return;
    if (r.ok) onDone(r.data);
    else setError(r.data.error || 'Could not upload. Please try again.');
  };

  return (
    <form className="site-admin-doc-upload" onSubmit={submit}>
      <div className="site-admin-pair">
        <label className="site-field">
          <span>Picture (JPEG, PNG, WebP or GIF, up to 20 MB)</span>
          <input type="file" accept="image/jpeg,image/png,image/webp,image/gif" onChange={pick} />
        </label>
        <label className="site-field">
          <span>Description <span className="site-admin-help">(what it shows, for people who can&apos;t see it)</span></span>
          <input type="text" value={title} maxLength={160} placeholder="Sidecar at the Wiscombe hairpin, 2026"
            onChange={e => { setTitle(e.target.value); if (!slugEdited) setSlug(slugify(e.target.value)); }} />
        </label>
      </div>
      <label className="site-field site-admin-image-slug">
        <span>Address <span className="site-admin-help">(can&apos;t be changed later)</span></span>
        <input type="text" value={slug} maxLength={60}
          onChange={e => { setSlug(e.target.value.toLowerCase()); setSlugEdited(true); }} />
        {slug && <span className="site-admin-help">/images/{slug}.jpg</span>}
      </label>
      <div className="site-admin-contact-foot">
        <span className="site-admin-meta">Big photos are made smaller, and their camera details (including where they were taken) removed.</span>
        {error && <span className="site-field-error" role="alert">{error}</span>}
        <button type="button" className="site-link-button" onClick={onCancel}>Cancel</button>
        <button type="submit" className="site-btn site-btn--ink"
          disabled={!file || file.size > MAX_BYTES || !title.trim() || !slug || saving}>
          {saving ? 'Uploading…' : 'Add image'}
        </button>
      </div>
    </form>
  );
}

function ImageCard({ img, session, onExpired, reload, onInsert }) {
  const [mode, setMode] = useState(null);     // null | 'replace' | 'describe'
  const [title, setTitle] = useState(img.title);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');

  const replace = async e => {
    const file = e.target.files[0];
    if (!file) return;
    if (file.size > MAX_BYTES) { setError('That file is over 20 MB.'); return; }
    setBusy(true);
    setError('');
    const form = new FormData();
    form.append('image', file);
    const r = await send(`/api/site-admin/images/${img.slug}`, session, onExpired, { method: 'POST', body: form });
    setBusy(false);
    if (!r) return;
    if (r.ok) { setMode(null); reload(); } else setError(r.data.error || 'Could not replace it.');
  };

  const describe = async e => {
    e.preventDefault();
    setBusy(true);
    const r = await send(`/api/site-admin/images/${img.slug}`, session, onExpired, {
      method: 'PUT', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ title }),
    });
    setBusy(false);
    if (!r) return;
    if (r.ok) { setMode(null); reload(); } else setError(r.data.error || 'Could not save it.');
  };

  const remove = async () => {
    if (!window.confirm(`Delete "${img.title}"? This can't be undone.`)) return;
    const r = await send(`/api/site-admin/images/${img.slug}`, session, onExpired, { method: 'DELETE' });
    if (!r) return;
    if (r.ok) reload(); else setError(r.data.error || 'Could not delete it.');
  };

  return (
    <li className="site-admin-image">
      <a href={img.url} target="_blank" rel="noreferrer" className="site-admin-image-thumb">
        <img src={thumb(img)} alt="" loading="lazy" />
      </a>
      <div className="site-admin-image-text">
        <strong>{img.title}</strong>
        <span className="site-admin-meta">
          {img.url} · {img.width}×{img.height} · {size(img.size_bytes)}
          {img.used_on.length > 0 && <> · used on {img.used_on.join(', ')}</>}
        </span>
        <div className="site-admin-doc-actions">
          {onInsert && <button type="button" className="site-btn site-btn--ink" onClick={() => onInsert(img)}>Insert image</button>}
          <button type="button" className="site-link-button" onClick={() => setMode(m => (m === 'replace' ? null : 'replace'))}>Replace</button>
          <button type="button" className="site-link-button" onClick={() => setMode(m => (m === 'describe' ? null : 'describe'))}>Edit description</button>
          {img.used_on.length === 0 && <button type="button" className="site-link-button" onClick={remove}>Delete</button>}
        </div>
        {mode === 'replace' && (
          <label className="site-field">
            <span>New picture: every page using this one shows it straight away, and the old one isn&apos;t kept</span>
            <input type="file" accept="image/jpeg,image/png,image/webp,image/gif" onChange={replace} disabled={busy} />
          </label>
        )}
        {mode === 'describe' && (
          <form className="site-admin-image-describe" onSubmit={describe}>
            <input type="text" value={title} maxLength={160} onChange={e => setTitle(e.target.value)} aria-label="Description" />
            <button type="submit" className="site-btn site-btn--ink" disabled={!title.trim() || title.replace(/\s+/g, ' ').trim() === img.title || busy}>Save</button>
          </form>
        )}
        {busy && <span className="site-admin-meta">Working…</span>}
        {error && <span className="site-field-error" role="alert">{error}</span>}
      </div>
    </li>
  );
}

// The Images panel. `onInsert(image)`: the page editor's Insert image (left
// out where there's no text to insert into). `open`: start open.
export default function PageImages({ images, error, reload, session, onExpired, onInsert, open }) {
  const [adding, setAdding] = useState(false);
  return (
    <details className="site-admin-page-docs" open={open}>
      <summary>Images{images ? ` · ${images.length}` : ''}</summary>
      <p className="site-admin-meta">
        Pictures for the pages. {onInsert ? 'Insert image puts one at the cursor in the text above. ' : ''}
        Replacing a picture changes it everywhere it&apos;s used. A picture can only be deleted when no page uses it.
      </p>
      {error && <p className="site-field-error">{error}</p>}
      {!images && !error && <p>Loading…</p>}
      {images && images.length > 0 && (
        <ul className="site-admin-images">
          {images.map(img => (
            <ImageCard key={img.slug} img={img} session={session} onExpired={onExpired} reload={reload} onInsert={onInsert} />
          ))}
        </ul>
      )}
      {adding ? (
        <AddForm session={session} onExpired={onExpired} onCancel={() => setAdding(false)}
          onDone={() => { setAdding(false); reload(); }} />
      ) : (
        <button type="button" className="site-btn site-btn--ghost-dark" onClick={() => setAdding(true)}>Add an image</button>
      )}
    </details>
  );
}

// Choose a home page photo: one of the uploaded images, or the original
// (fixed) photo. `value` is the chosen address ('' for the original).
export function PhotoPicker({ label, images, value, original, onPick }) {
  const current = value ? images?.find(i => i.url === value) : null;
  return (
    <div className="site-field site-admin-photo-picker">
      <span>{label}</span>
      <div className="site-admin-photo-current">
        <img src={current ? thumb(current) : (value || original)} alt="" />
        <span className="site-admin-meta">
          {value ? (current ? current.title : `${value} (no longer uploaded: choose another)`) : 'The original photo'}
        </span>
      </div>
      <details>
        <summary>Choose a different photo</summary>
        <ul className="site-admin-photo-choices">
          <li>
            <button type="button" onClick={() => onPick(null)} aria-pressed={!value}>
              <img src={original} alt="" loading="lazy" /><span>The original photo</span>
            </button>
          </li>
          {(images || []).map(img => (
            <li key={img.slug}>
              <button type="button" onClick={() => onPick(img)} aria-pressed={value === img.url}>
                <img src={thumb(img)} alt="" loading="lazy" /><span>{img.title}</span>
              </button>
            </li>
          ))}
        </ul>
        <p className="site-admin-help">To use a new photo, add it in Images at the bottom of this form first.</p>
      </details>
    </div>
  );
}
